// Agreed admin wire contract. All timestamps are ISO 8601 strings.
export type DeletionState =
  | "requested"
  | "awaitingUserAction"
  | "settling"
  | "erasing"
  | "completed";
export type DeletionStep =
  | "wallet"
  | "cards"
  | "ramps"
  | "earn"
  | "providers"
  | "warehouse"
  | "backups"
  | "retention"
  | "identityRecords"
  | "receipt";
export type DeletionOutcome = "ready" | "erased" | "retained";
export type DeletionDomain = "wallet" | "cards" | "ramps" | "earn";
export interface DeletionRequest {
  id: string;
  user: string;
  state: DeletionState;
  confirmationVersion: string;
  requestedAt: string;
  responseDueAt: string;
  completedAt: string | null;
  updatedAt: string;
}
export interface DeletionEvidence {
  id: string;
  requestId: string;
  step: DeletionStep;
  outcome: DeletionOutcome;
  evidenceReference: string;
  retentionPolicy: string | null;
  retentionUntil: string | null;
  reviewedBy: string;
  createdAt: string;
}
export interface DeletionDomainState {
  id: string;
  requestId: string;
  userId: string;
  domain: string;
  status: string;
  access: string;
  reasonCodes: string[];
  backupErased: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: null;
}
export interface DeletionBlocker {
  phase: "finalize" | "identityErasure" | "finalNotice" | "completion";
  code: string;
  domain: DeletionDomain | null;
  step: DeletionStep | null;
  message: string;
}
export interface DeletionAdminAction {
  id: string;
  requestId: string;
  action: "review" | "finalize" | "verifyIntake" | "dismissIntake" | "identityErased";
  actor: string;
  expectedUpdatedAt: string;
  evidenceReference: string;
  createdAt: string;
}
export interface DeletionCase {
  request: DeletionRequest;
  domains: { domain: DeletionDomain; assessment: DeletionDomainState | null }[]; // Always four slots.
  reviews: { step: DeletionStep; evidence: DeletionEvidence | null }[]; // Always ten slots.
  blockers: DeletionBlocker[];
  canFinalize: boolean;
  canRecordFinalNotice: boolean;
  identityErasedAt: string | null;
  finalization: DeletionAdminAction | null;
}
export interface DeletionIntake {
  id: string;
  email: string | null;
  receivedAt: string;
  responseDueAt: string;
  linkedRequestId: string | null;
  verificationEvidenceReference: string | null;
  verifiedBy: string | null;
  verifiedAt: string | null;
  updatedAt: string;
}
export interface DeletionPage<T> {
  items: T[];
  nextCursor: string | null;
}
export interface DeletionReviewInput {
  clientRequestId: string;
  step: DeletionStep;
  outcome: DeletionOutcome;
  evidenceReference: string;
  retentionPolicy: string | null;
  retentionUntil: string | null;
}
export interface DeletionFinalizeInput {
  clientRequestId: string;
  evidenceReference: string;
}
export interface DeletionVerifyInput {
  clientRequestId: string;
  userId: string;
  evidenceReference: string;
}
export interface DeletionDismissInput {
  clientRequestId: string;
  evidenceReference: string;
}

import { adminRequest } from "./client";

export const DELETION_STEPS: DeletionStep[] = [
  "wallet",
  "cards",
  "ramps",
  "earn",
  "providers",
  "warehouse",
  "backups",
  "retention",
  "identityRecords",
  "receipt",
];
export const DELETION_DOMAINS: DeletionDomain[] = ["wallet", "cards", "ramps", "earn"];
export const ACTIVE_DELETION_STATES: DeletionState[] = [
  "requested",
  "awaitingUserAction",
  "settling",
  "erasing",
];

export interface DeletionPageOptions {
  cursor?: string;
  pageSize?: number;
  signal?: AbortSignal;
}
const requestPath = (id: string) => `/accountDeletionRequests/${encodeURIComponent(id)}`;

export function getDeletionCase(
  token: string,
  id: string,
  signal?: AbortSignal,
): Promise<DeletionCase> {
  return adminRequest({ method: "GET", path: requestPath(id), token, signal });
}
export function getDeletionReviews(
  token: string,
  id: string,
  { signal, ...query }: DeletionPageOptions = {},
): Promise<DeletionPage<DeletionEvidence>> {
  return adminRequest({ method: "GET", path: `${requestPath(id)}/reviews`, query, token, signal });
}
export function getDeletionActions(
  token: string,
  id: string,
  { signal, ...query }: DeletionPageOptions = {},
): Promise<DeletionPage<DeletionAdminAction>> {
  return adminRequest({ method: "GET", path: `${requestPath(id)}/actions`, query, token, signal });
}
export function getDeletionIntakes(
  token: string,
  { signal, ...query }: DeletionPageOptions = {},
): Promise<DeletionPage<DeletionIntake>> {
  return adminRequest({ method: "GET", path: "/accountDeletionIntakes", query, token, signal });
}
export function getDeletionRetention(
  token: string,
  before: string,
  { signal, ...query }: DeletionPageOptions = {},
): Promise<DeletionPage<DeletionEvidence>> {
  return adminRequest({
    method: "GET",
    path: "/accountDeletionRequests/retained",
    query: { ...query, before },
    token,
    signal,
  });
}
export function reviewDeletion(
  token: string,
  id: string,
  updatedAt: string,
  body: DeletionReviewInput,
): Promise<DeletionRequest> {
  return adminRequest({
    method: "POST",
    path: `${requestPath(id)}/reviews`,
    query: { updatedAt },
    body,
    token,
  });
}
export function finalizeDeletion(
  token: string,
  id: string,
  updatedAt: string,
  body: DeletionFinalizeInput,
): Promise<DeletionRequest> {
  return adminRequest({
    method: "POST",
    path: `${requestPath(id)}/finalize`,
    query: { updatedAt },
    body,
    token,
  });
}
export function verifyDeletionIntake(
  token: string,
  id: string,
  updatedAt: string,
  body: DeletionVerifyInput,
): Promise<DeletionRequest> {
  return adminRequest({
    method: "POST",
    path: `/accountDeletionIntakes/${encodeURIComponent(id)}/verify`,
    query: { updatedAt },
    body,
    token,
  });
}
export function dismissDeletionIntake(
  token: string,
  id: string,
  updatedAt: string,
  body: DeletionDismissInput,
): Promise<void> {
  return adminRequest({
    method: "POST",
    path: `/accountDeletionIntakes/${encodeURIComponent(id)}/dismiss`,
    query: { updatedAt },
    body,
    token,
  });
}
